/**
 * API Client for Sovrun Backend
 *
 * Provides typed methods for interacting with the strategies,
 * allocations, and portfolio API endpoints.
 */

import type {
  AllocateRequest,
  AllocateResponse,
  AllocationsListResponse,
  DeallocateRequest,
  DeallocateResponse,
  PerformanceDataResponse,
  PortfolioEquityCurveResponse,
  PortfolioSummary,
  StrategiesListResponse,
  Strategy,
  StrategyAllocation,
  StrategyFilters,
} from "@/types/api.types";
import type {
  CreateOrderRequest,
  ModifyOrderRequest,
  Order,
  OrderQueryParams,
  OrdersListResponse,
  Position,
  PositionQueryParams,
  PositionsListResponse,
  AccountSummary,
  CreateSmartOrderRequest,
  SmartOrder,
  SmartOrderQueryParams,
  SmartOrdersListResponse,
  SmartOrderProgress,
  ExecutionStatus,
} from "@/types/execution.types";

// API Configuration
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
const DEFAULT_TIMEOUT = 10000;

// ============================================================================
// Tenant Context (SFWL-002)
// ============================================================================

/**
 * Active white-label tenant id, registered by TenantThemeProvider after
 * subdomain resolution. The Next.js middleware used to inject `x-tenant-id`
 * on proxied requests; in the Vite SPA the API client attaches it directly.
 */
let activeTenantId: string | null = null;

export function setActiveTenantId(tenantId: string | null): void {
  activeTenantId = tenantId;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from filter parameters
 */
function buildQueryString(params?: Record<string, string | undefined>): string {
  if (!params) return "";

  const validParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== "all")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
    );

  return validParams.length > 0 ? `?${validParams.join("&")}` : "";
}

/**
 * Fetch wrapper with timeout and error handling
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const headers = new Headers(options.headers);
  if (activeTenantId && !headers.has("x-tenant-id")) {
    headers.set("x-tenant-id", activeTenantId);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Handle API response and throw on error
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Response body is not JSON, use default message
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

// ============================================================================
// Strategy API
// ============================================================================

export const strategiesApi = {
  /**
   * List all strategies with optional filters
   */
  async list(filters?: StrategyFilters): Promise<StrategiesListResponse> {
    const queryParams = filters
      ? {
          marketView: filters.marketView,
          riskLevel: filters.riskLevel,
          assetClass: filters.assetClass,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/strategies${queryString}`
    );
    return handleResponse<StrategiesListResponse>(response);
  },

  /**
   * Get a single strategy by ID
   */
  async get(id: string): Promise<Strategy> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/strategies/${id}`);
    return handleResponse<Strategy>(response);
  },

  /**
   * Get strategy performance history
   */
  async getPerformance(id: string, days = 365): Promise<PerformanceDataResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/strategies/${id}/performance?days=${days}`
    );
    return handleResponse<PerformanceDataResponse>(response);
  },
};

// ============================================================================
// Allocations API
// ============================================================================

export const allocationsApi = {
  /**
   * List allocations for the authenticated user (session cookie)
   * Falls back to wallet address if provided
   */
  async list(walletAddress?: string): Promise<AllocationsListResponse> {
    const url = walletAddress
      ? `${API_BASE_URL}/api/allocations?walletAddress=${encodeURIComponent(walletAddress)}`
      : `${API_BASE_URL}/api/allocations`;
    const response = await fetchWithTimeout(url);
    // Backend returns AllocationDto[] directly, wrap it
    const allocations = await handleResponse<StrategyAllocation[]>(response);
    return { allocations };
  },

  /**
   * Create a new allocation
   */
  async create(data: AllocateRequest): Promise<AllocateResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/allocations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<AllocateResponse>(response);
  },

  /**
   * Deallocate (withdraw) from an allocation
   */
  async deallocate(id: string, data: DeallocateRequest): Promise<DeallocateResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/allocations/${id}/deallocate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<DeallocateResponse>(response);
  },

  /**
   * Get portfolio summary for the authenticated user (session cookie)
   * Falls back to wallet address if provided
   */
  async getPortfolio(walletAddress?: string): Promise<PortfolioSummary> {
    // Portfolio summary — session cookie handles auth, wallet is optional fallback
    const portfolioUrl = walletAddress
      ? `${API_BASE_URL}/api/allocations/portfolio?walletAddress=${encodeURIComponent(walletAddress)}`
      : `${API_BASE_URL}/api/allocations/portfolio`;

    const portfolioRes = await fetchWithTimeout(portfolioUrl);

    const data = await handleResponse<
      Omit<PortfolioSummary, "availableBalance"> & { allocations?: unknown[] }
    >(portfolioRes);

    return {
      totalAllocated: data.totalAllocated,
      totalCurrentValue: data.totalCurrentValue,
      totalUnrealizedPnl: data.totalUnrealizedPnl,
      totalUnrealizedPnlPercent: data.totalUnrealizedPnlPercent,
      totalRealizedPnl: data.totalRealizedPnl,
      allocationCount: data.allocationCount,
      availableBalance: null, // Will be populated from account data
    };
  },

  /**
   * Get the portfolio-performance-over-time equity curve for the
   * authenticated user (session cookie). Falls back to wallet address if
   * provided. `equityCurve` is empty when the user has no active
   * allocations or no strategy performance data yet — not an error.
   */
  async getEquityCurve(walletAddress?: string): Promise<PortfolioEquityCurveResponse> {
    const url = walletAddress
      ? `${API_BASE_URL}/api/allocations/portfolio/equity-curve?walletAddress=${encodeURIComponent(walletAddress)}`
      : `${API_BASE_URL}/api/allocations/portfolio/equity-curve`;

    const response = await fetchWithTimeout(url);
    return handleResponse<PortfolioEquityCurveResponse>(response);
  },
};

// ============================================================================
// Orders API (CQG Integration)
// ============================================================================

export const ordersApi = {
  /**
   * List orders with optional filters
   */
  async list(params?: OrderQueryParams): Promise<OrdersListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          side: params.side,
          status: params.status,
          brokerId: params.brokerId,
          accountId: params.accountId,
          strategyId: params.strategyId,
          limit: params.limit?.toString(),
          offset: params.offset?.toString(),
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders${queryString}`
    );
    return handleResponse<OrdersListResponse>(response);
  },

  /**
   * Get a single order by ID
   */
  async get(orderId: string): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`
    );
    return handleResponse<Order>(response);
  },

  /**
   * Create a new order
   */
  async create(data: CreateOrderRequest): Promise<Order> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/cqg/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<Order>(response);
  },

  /**
   * Modify an existing order
   */
  async modify(orderId: string, data: ModifyOrderRequest): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<Order>(response);
  },

  /**
   * Cancel an order
   */
  async cancel(orderId: string): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/${orderId}`,
      {
        method: "DELETE",
      }
    );
    return handleResponse<Order>(response);
  },

  /**
   * Bulk cancel orders
   */
  async bulkCancel(params?: {
    symbol?: string;
    side?: string;
    accountId?: string;
    cancelAll?: boolean;
  }): Promise<{ canceledCount: number; orders: Order[] }> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/orders/bulk-cancel`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params || {}),
      }
    );
    return handleResponse<{ canceledCount: number; orders: Order[] }>(response);
  },
};

// ============================================================================
// Positions API (CQG Integration)
// ============================================================================

export const positionsApi = {
  /**
   * List positions with optional filters
   */
  async list(params?: PositionQueryParams): Promise<PositionsListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          side: params.side,
          brokerId: params.brokerId,
          accountId: params.accountId,
          strategyId: params.strategyId,
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions${queryString}`
    );
    return handleResponse<PositionsListResponse>(response);
  },

  /**
   * Get a position by symbol
   */
  async getBySymbol(symbol: string, accountId?: string): Promise<Position> {
    const queryString = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions/${encodeURIComponent(symbol)}${queryString}`
    );
    return handleResponse<Position>(response);
  },

  /**
   * Get account summary
   */
  async getAccountSummary(accountId: string): Promise<AccountSummary> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/accounts/${accountId}/summary`
    );
    return handleResponse<AccountSummary>(response);
  },

  /**
   * Close a position (market order to flatten)
   */
  async closePosition(
    symbol: string,
    accountId: string,
    quantity?: number
  ): Promise<Order> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/positions/${encodeURIComponent(symbol)}/close`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountId, quantity }),
      }
    );
    return handleResponse<Order>(response);
  },
};

// ============================================================================
// Smart Orders API (CQG Integration)
// ============================================================================

export const smartOrdersApi = {
  /**
   * List smart orders with optional filters
   */
  async list(params?: SmartOrderQueryParams): Promise<SmartOrdersListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          smartOrderType: params.smartOrderType,
          status: params.status,
          accountId: params.accountId,
          walletAddress: params.walletAddress,
          limit: params.limit?.toString(),
          offset: params.offset?.toString(),
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders${queryString}`
    );
    return handleResponse<SmartOrdersListResponse>(response);
  },

  /**
   * Get a single smart order by ID
   */
  async get(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}`
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Get smart order progress
   */
  async getProgress(id: string): Promise<SmartOrderProgress> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/progress`
    );
    return handleResponse<SmartOrderProgress>(response);
  },

  /**
   * Create a new smart order
   */
  async create(data: CreateSmartOrderRequest): Promise<SmartOrder> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/cqg/smart-orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Pause a smart order
   */
  async pause(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/pause`,
      {
        method: "POST",
      }
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Resume a paused smart order
   */
  async resume(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/resume`,
      {
        method: "POST",
      }
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Cancel a smart order
   */
  async cancel(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}`,
      {
        method: "DELETE",
      }
    );
    return handleResponse<SmartOrder>(response);
  },
};

// ============================================================================
// Execution Status API
// ============================================================================

export const executionStatusApi = {
  /**
   * Get execution system status
   */
  async getStatus(): Promise<ExecutionStatus> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/cqg/status`);
    return handleResponse<ExecutionStatus>(response);
  },
};

// ============================================================================
// Organizations API (Custom Hierarchy Endpoints)
// ============================================================================

export interface OrgRecord {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
  tenantId: string;
  parentId: string | null;
  createdAt: string;
}

export interface MemberRecord {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  createdAt: string;
  userName?: string;
  userEmail?: string;
}

export const organizationsApi = {
  async get(orgId: string): Promise<OrgRecord> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/organizations/${orgId}`);
    return handleResponse<OrgRecord>(response);
  },

  async getChildren(orgId: string): Promise<OrgRecord[]> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/organizations/${orgId}/children`
    );
    return handleResponse<OrgRecord[]>(response);
  },

  async getTree(orgId: string): Promise<OrgRecord[]> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/organizations/${orgId}/tree`
    );
    return handleResponse<OrgRecord[]>(response);
  },

  async createChild(
    orgId: string,
    data: { name: string; slug?: string; logo?: string }
  ): Promise<OrgRecord> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/organizations/${orgId}/create-child`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<OrgRecord>(response);
  },

  async getMembersCascaded(orgId: string): Promise<MemberRecord[]> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/organizations/${orgId}/members-cascaded`
    );
    return handleResponse<MemberRecord[]>(response);
  },

  async createPersonal(name: string): Promise<OrgRecord> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/organizations/create-personal`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }
    );
    return handleResponse<OrgRecord>(response);
  },
};

// ============================================================================
// Portfolio Builder API
// ============================================================================

import type {
  InvestorProfileRequest,
  RecommendResult,
  ApplyPortfolioRequest,
  ApplyPortfolioResponse,
} from "@/types/portfolio-builder.types";

export const portfolioBuilderApi = {
  async recommend(profile: InvestorProfileRequest): Promise<RecommendResult> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/portfolio-builder/recommend`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      }
    );
    return handleResponse<RecommendResult>(response);
  },

  async apply(data: ApplyPortfolioRequest): Promise<ApplyPortfolioResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/portfolio-builder/apply`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<ApplyPortfolioResponse>(response);
  },
};

// ============================================================================
// Market Data API
// ============================================================================

export type AssetClass = "future" | "future_option" | "crypto" | "equity";

export interface AssetDto {
  id: string;
  symbol: string;
  displaySymbol: string;
  assetClass: AssetClass;
  exchange: string;
  contractSize: number;
  tickSize: number;
  currency: string;
  metadata: Record<string, unknown> | null;
}

export interface AssetsListResponse {
  assets: AssetDto[];
  count: number;
  scope: "futures" | "crypto" | "all";
  tenantId: string | null;
}

export type Timeframe = "1m" | "5m" | "15m" | "1h" | "4h" | "1d" | "1w" | "1M";

export interface MarketDataCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface TenantCandlesResponse {
  symbol: string;
  interval: Timeframe;
  providerId: string;
  candles: MarketDataCandle[];
}

// --- SFWL-005 Phase 2: market-data providers + BYOD credentials ---

export interface MarketDataProviderInfo {
  id: string;
  displayName: string;
  requiresCredential: boolean;
  capabilities: {
    historicalCandles: boolean;
    realtimeQuotes: boolean;
    realtimeTrades: boolean;
    level2: boolean;
    assetClasses: string[];
  };
}

export interface ProvidersListResponse {
  providers: MarketDataProviderInfo[];
}

export type MarketDataCredentialStatus = "unverified" | "valid" | "invalid";

/** Masked read of a stored org credential. Secrets are reduced to `****abcd`. */
export interface MarketDataCredentialDto {
  id: string;
  providerId: string;
  status: MarketDataCredentialStatus;
  source: "org";
  lastValidatedAt: string | null;
  maskedPayload: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface CredentialsListResponse {
  credentials: MarketDataCredentialDto[];
}

export interface ValidateCredentialResponse {
  status: MarketDataCredentialStatus;
  error?: string;
}

export const marketDataApi = {
  /**
   * SFWL-004: List the tradeable asset universe for the active tenant.
   * Tenant scoping is done server-side via the x-tenant-id header set by
   * the Next.js middleware.
   */
  async listAssets(): Promise<AssetsListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/market-data/assets`);
    return handleResponse<AssetsListResponse>(response);
  },

  /**
   * SFWL-005: Tenant-scoped candle fetch via the backend provider registry.
   * Replaces the previous direct browser → Hyperliquid call so BYOD
   * vendor credentials never have to touch the browser.
   *
   * Honors the caller's AbortSignal directly (skips fetchWithTimeout's
   * internal controller so component cleanup actually cancels in-flight
   * requests when market/timeframe changes).
   */
  async getCandles(args: {
    symbol: string;
    interval: Timeframe;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<TenantCandlesResponse> {
    const params = new URLSearchParams({
      symbol: args.symbol,
      interval: args.interval,
    });
    if (args.limit) params.set("limit", String(args.limit));

    const response = await fetch(
      `${API_BASE_URL}/api/market-data/candles?${params.toString()}`,
      { credentials: "include", signal: args.signal }
    );
    return handleResponse<TenantCandlesResponse>(response);
  },

  /**
   * SFWL-005: List registered providers + capabilities. Public (no auth).
   * Drives the MarketDataSettings panel's provider rows.
   */
  async listProviders(): Promise<ProvidersListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/market-data/providers`);
    return handleResponse<ProvidersListResponse>(response);
  },

  /**
   * SFWL-005 Phase 2: list the active org's BYOD credentials (masked).
   * Org-admin scoped; the Sovrun-fallback row is never returned here.
   */
  async listCredentials(): Promise<CredentialsListResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials`
    );
    return handleResponse<CredentialsListResponse>(response);
  },

  /**
   * Create or replace the org credential for a provider. `payload` is the
   * opaque vendor shape (e.g. CQG `{ username, password, clientAppId }`);
   * it's encrypted at rest and never returned in plaintext.
   */
  async upsertCredential(
    providerId: string,
    payload: Record<string, unknown>
  ): Promise<MarketDataCredentialDto> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ providerId, payload }),
      }
    );
    return handleResponse<MarketDataCredentialDto>(response);
  },

  /** Run a credential's auth ping and return its new status. */
  async validateCredential(id: string): Promise<ValidateCredentialResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials/${id}/validate`,
      { method: "POST" }
    );
    return handleResponse<ValidateCredentialResponse>(response);
  },

  /** Delete an org credential (falls the org back to the Sovrun default). */
  async deleteCredential(id: string): Promise<void> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials/${id}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },
};

// ============================================================================
// Broker Credentials API (SFWL-006 Phase 2)
// ============================================================================

export type BrokerId = "cqg";

/** Masked read of a stored org broker credential. Secrets are `****abcd`. */
export interface BrokerCredentialDto {
  id: string;
  broker: BrokerId;
  status: MarketDataCredentialStatus;
  lastValidatedAt: string | null;
  maskedPayload: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface BrokerCredentialsListResponse {
  credentials: BrokerCredentialDto[];
}

/**
 * Tenant scoping: the `x-tenant-id` header is attached globally by
 * `fetchWithTimeout` from the `activeTenantId` registered by
 * TenantThemeProvider — the controller 400s without an active tenant, so these
 * calls only succeed on a resolved tenant subdomain.
 */
export const brokerCredentialsApi = {
  /** List the active org's BYO broker credentials (masked). Org-admin scoped. */
  async list(): Promise<BrokerCredentialsListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/broker-credentials`);
    return handleResponse<BrokerCredentialsListResponse>(response);
  },

  /**
   * Create or replace the org credential for a broker. `payload` is the opaque
   * vendor shape (CQG `{ username, password, clientAppId }`); encrypted at rest.
   */
  async upsert(
    broker: BrokerId,
    payload: Record<string, unknown>
  ): Promise<BrokerCredentialDto> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/broker-credentials`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ broker, payload }),
    });
    return handleResponse<BrokerCredentialDto>(response);
  },

  /** Run the credential's "test connection" check and return its new status. */
  async validate(id: string): Promise<ValidateCredentialResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/broker-credentials/${id}/validate`,
      { method: "POST" }
    );
    return handleResponse<ValidateCredentialResponse>(response);
  },

  /** Delete an org broker credential. */
  async remove(id: string): Promise<void> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/broker-credentials/${id}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },
};

// ============================================================================
// Combined API Export
// ============================================================================

export const api = {
  strategies: strategiesApi,
  allocations: allocationsApi,
  orders: ordersApi,
  positions: positionsApi,
  smartOrders: smartOrdersApi,
  executionStatus: executionStatusApi,
  organizations: organizationsApi,
  portfolioBuilder: portfolioBuilderApi,
  marketData: marketDataApi,
  brokerCredentials: brokerCredentialsApi,
};
